import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import {
  AuthApiService,
  LoginRequest,
  LoginResponse,
} from '../../features/auth/services/auth-api.service';
import { AuthTokenService } from './auth-token.service';

export interface LoginFormData {
  username: string;
  password: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userData = signal<User | null>(null);
  private readonly STORAGE_USER = 'userData';
  private refreshTimer: any = null;
  private isRefreshing = false;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private authApi = inject(AuthApiService);
  private authTokenService = inject(AuthTokenService);
  private router = inject(Router);

  constructor() {
    if (this.isBrowser) {
      this.initializeAuthState();
      this.startTokenRefreshTimer();
    }
  }

  private initializeAuthState(): void {
    const token = this.authTokenService.getAccessToken();
    const savedUserData = localStorage.getItem(this.STORAGE_USER);

    if (token && savedUserData) {
      try {
        const userData = JSON.parse(savedUserData);
        this.userData.set(userData);

        if (this.authTokenService.isAccessTokenExpired()) {
          this.refreshTokenSilently();
        }
      } catch (error) {
        this.clearAuthData();
      }
    }
  }

  private startTokenRefreshTimer(): void {
    if (!this.isBrowser) return;

    this.refreshTimer = setInterval(() => {
      if (this.isAuthenticated() && !this.isRefreshing) {
        if (this.authTokenService.isAccessTokenExpiringSoon(3)) {
          this.refreshTokenSilently();
        }
      }
    }, 30000);
  }

  private async refreshTokenSilently(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }

    const currentRefreshToken = this.authTokenService.getRefreshToken();

    if (!currentRefreshToken) {
      await this.handleSessionExpired();
      return;
    }

    if (this.authTokenService.isRefreshTokenExpired()) {
      await this.handleSessionExpired();
      return;
    }

    this.isRefreshing = true;

    try {
      const response = await this.authApi.refreshToken(currentRefreshToken);

      this.authTokenService.saveTokens({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
        refreshExpiresIn: response.refreshExpiresIn
      });
    } catch (error) {
      await this.handleSessionExpired();
    } finally {
      this.isRefreshing = false;
    }
  }

  private async handleSessionExpired(): Promise<void> {
    this.clearAuthData();
    await this.router.navigate(['/login'], {
      queryParams: { expired: 'true' }
    });
  }

  private clearAuthData(): void {
    if (this.isBrowser) {
      localStorage.removeItem(this.STORAGE_USER);

      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    }
    this.authTokenService.clearTokens();
    this.userData.set(null);
    this.isRefreshing = false;
  }

  async login(credentials: LoginFormData): Promise<void> {
    try {
      const loginPayload = {
        username: credentials.username,
        password: credentials.password,
      };

      const response = await this.authApi.login(loginPayload);

      this.authTokenService.saveTokens({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
        refreshExpiresIn: response.refreshExpiresIn
      });

      const userData = {
        id: response.user.id.toString(),
        username: response.user.username,
        email: response.user.email,
        name: response.user.name,
        role: response.user.roles[0] || 'USER',
      };

      this.userData.set(userData);

      if (this.isBrowser) {
        localStorage.setItem(this.STORAGE_USER, JSON.stringify(userData));
      }

      if (!this.refreshTimer) {
        this.startTokenRefreshTimer();
      }
    } catch (error) {
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      // await this.authApi.logout();
    } catch (error) {
      // Silent error
    } finally {
      this.clearAuthData();
    }
  }

  async forceRefreshToken(): Promise<boolean> {
    try {
      await this.refreshTokenSilently();
      return true;
    } catch (error) {
      return false;
    }
  }

  getAccessToken(): string | null {
    return this.authTokenService.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.authTokenService.getRefreshToken();
  }

  getCurrentUser(): User | null {
    return this.userData();
  }

  getUserData(): User | null {
    const signalData = this.userData();
    if (signalData) {
      return signalData;
    }

    if (this.isBrowser) {
      const userData = localStorage.getItem(this.STORAGE_USER);
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          this.userData.set(parsed);
          return parsed;
        } catch (error) {
          return null;
        }
      }
    }
    return null;
  }

  getUserRole(): string | null {
    const user = this.getUserData();
    return user?.role || null;
  }

  isAuthenticated(): boolean {
    const hasToken = this.authTokenService.isAuthenticated();
    const userData = this.getUserData();
    return hasToken && !!userData;
  }

  getTokenTimeRemaining(): number {
    return this.authTokenService.getAccessTokenTimeRemaining();
  }

  isTokenExpiringSoon(minutes: number = 3): boolean {
    return this.authTokenService.isAccessTokenExpiringSoon(minutes);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }
}
